<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { conversationAnalysisAPI } from 'dashboard/api/iaReports';

const today = new Date();
const defaultFrom = new Date(today);
defaultFrom.setDate(today.getDate() - 29);

const fromDate = ref(defaultFrom.toISOString().slice(0, 10));
const toDate = ref(today.toISOString().slice(0, 10));
const agentFilter = ref('');

const loading = ref(false);
const performance = ref(null);

const OUTCOME_STYLES = {
  converted: {
    label: 'Convertido',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200',
    icon: 'i-lucide-check-circle',
  },
  lost: {
    label: 'Perdido',
    color: 'text-red-600',
    bg: 'bg-red-50 dark:bg-red-900/20 border-red-200',
    icon: 'i-lucide-x-circle',
  },
  abandoned: {
    label: 'Abandonado',
    color: 'text-amber-600',
    bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200',
    icon: 'i-lucide-ghost',
  },
  inconclusive: {
    label: 'Inconclusivo',
    color: 'text-n-slate-9',
    bg: 'bg-n-alpha-2 border-n-weak',
    icon: 'i-lucide-help-circle',
  },
};

const LOSS_STYLES = {
  price: { label: 'Preco', icon: 'i-lucide-dollar-sign' },
  timing: { label: 'Timing', icon: 'i-lucide-clock' },
  trust: { label: 'Confianca', icon: 'i-lucide-shield-alert' },
  need: { label: 'Necessidade', icon: 'i-lucide-target' },
  competition: { label: 'Concorrencia', icon: 'i-lucide-swords' },
  other: { label: 'Outro', icon: 'i-lucide-circle-help' },
};

async function fetchPerformance() {
  loading.value = true;
  try {
    const params = { from: fromDate.value, to: toDate.value };
    if (agentFilter.value) params.agent_id = agentFilter.value;
    const res = await conversationAnalysisAPI.getPerformance(params);
    performance.value = res.data;
  } catch {
    performance.value = null;
  } finally {
    loading.value = false;
  }
}

onMounted(fetchPerformance);
watch([fromDate, toDate, agentFilter], fetchPerformance);

const kpiCards = computed(() => {
  if (!performance.value) return [];
  const p = performance.value;
  return [
    {
      label: 'Total Analisado',
      value: p.total_analyzed ?? 0,
      color: 'text-n-slate-12',
      bg: 'bg-n-alpha-2 border-n-weak',
      icon: 'i-lucide-file-search',
    },
    {
      label: 'Taxa de Conversao',
      value: `${p.conversion_rate ?? 0}%`,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200',
      icon: 'i-lucide-trending-up',
    },
    {
      label: 'Score Medio (IA)',
      value: p.avg_ai_score != null ? p.avg_ai_score.toFixed(1) : '-',
      color: 'text-blue-600',
      bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200',
      icon: 'i-lucide-brain',
    },
    {
      label: 'Objetivo Atingido',
      value: `${p.objective_met_rate ?? 0}%`,
      color: 'text-violet-600',
      bg: 'bg-violet-50 dark:bg-violet-900/20 border-violet-200',
      icon: 'i-lucide-target',
    },
    {
      label: 'Sentimento Medio',
      value: p.avg_sentiment != null ? p.avg_sentiment.toFixed(2) : '-',
      color: 'text-n-slate-12',
      bg: 'bg-n-alpha-2 border-n-weak',
      icon: 'i-lucide-heart',
    },
    {
      label: 'Propostas Pendentes',
      value: p.pending_proposals ?? 0,
      color: p.pending_proposals > 0 ? 'text-amber-600' : 'text-n-slate-9',
      bg:
        p.pending_proposals > 0
          ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200'
          : 'bg-n-alpha-2 border-n-weak',
      icon: 'i-lucide-lightbulb',
    },
  ];
});

const outcomeCards = computed(() => {
  if (!performance.value?.outcomes) return [];
  return Object.entries(performance.value.outcomes).map(([key, count]) => {
    const style = OUTCOME_STYLES[key] || OUTCOME_STYLES.inconclusive;
    return { ...style, count, key };
  });
});

const lossCategories = computed(() => {
  if (!performance.value?.top_loss_categories) return [];
  const total = Object.values(performance.value.top_loss_categories).reduce(
    (s, v) => s + v,
    0
  );
  return Object.entries(performance.value.top_loss_categories)
    .sort(([, a], [, b]) => b - a)
    .map(([key, count]) => ({
      ...(LOSS_STYLES[key] || LOSS_STYLES.other),
      key,
      count,
      pct: total > 0 ? ((count / total) * 100).toFixed(0) : 0,
    }));
});

const scoreDistribution = computed(() => {
  if (!performance.value?.score_distribution) return [];
  const d = performance.value.score_distribution;
  const total = (d.high || 0) + (d.mid || 0) + (d.low || 0);
  return [
    {
      label: 'Alto (7-10)',
      count: d.high || 0,
      pct: total ? (((d.high || 0) / total) * 100).toFixed(0) : 0,
      color: 'bg-emerald-500',
    },
    {
      label: 'Medio (5-7)',
      count: d.mid || 0,
      pct: total ? (((d.mid || 0) / total) * 100).toFixed(0) : 0,
      color: 'bg-amber-400',
    },
    {
      label: 'Baixo (0-5)',
      count: d.low || 0,
      pct: total ? (((d.low || 0) / total) * 100).toFixed(0) : 0,
      color: 'bg-red-500',
    },
  ];
});

const digests = computed(() => performance.value?.recent_digests || []);
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <!-- Header -->
    <div class="flex items-center justify-between flex-wrap gap-3">
      <div>
        <h1 class="text-xl font-bold text-n-slate-12">Performance Comercial</h1>
        <p class="text-sm text-n-slate-10 mt-0.5">
          Analise automatica de cada conversa resolvida — 7 dimensoes Full Sales
        </p>
      </div>

      <div
        class="flex items-center gap-2 bg-white dark:bg-n-alpha-2 border border-n-weak rounded-lg px-3 py-2"
      >
        <span class="i-lucide-calendar w-4 h-4 text-n-slate-10" />
        <input
          v-model="fromDate"
          type="date"
          class="text-sm bg-transparent text-n-slate-12 outline-none"
        />
        <span class="text-n-slate-9">-</span>
        <input
          v-model="toDate"
          type="date"
          class="text-sm bg-transparent text-n-slate-12 outline-none"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-16">
      <span class="i-lucide-loader-2 w-6 h-6 text-n-slate-10 animate-spin" />
      <span class="ml-2 text-n-slate-10 text-sm">Carregando dados de performance...</span>
    </div>

    <!-- Empty -->
    <div
      v-else-if="!performance"
      class="flex flex-col items-center py-16 text-n-slate-10"
    >
      <span class="i-lucide-bar-chart-3 w-12 h-12 mb-3 opacity-40" />
      <p class="text-sm">
        Nenhum dado de performance disponivel para o periodo selecionado.
      </p>
    </div>

    <template v-else>
      <!-- KPI Cards -->
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <div
          v-for="card in kpiCards"
          :key="card.label"
          class="flex flex-col gap-2 p-3 rounded-xl border shadow-sm"
          :class="[card.bg]"
        >
          <div class="flex items-center justify-between">
            <p class="text-xs font-medium text-n-slate-10">{{ card.label }}</p>
            <span class="w-3.5 h-3.5" :class="[card.icon, card.color]" />
          </div>
          <p class="text-xl font-bold" :class="[card.color]">
            {{ card.value }}
          </p>
        </div>
      </div>

      <!-- Two-column layout: outcomes + loss categories -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <!-- Outcomes -->
        <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm p-4">
          <h2 class="text-sm font-semibold text-n-slate-12 mb-3">
            Resultado das Conversas
          </h2>
          <div class="grid grid-cols-2 gap-3">
            <div
              v-for="item in outcomeCards"
              :key="item.key"
              class="flex items-center gap-3 p-3 rounded-lg border"
              :class="[item.bg]"
            >
              <span class="w-5 h-5" :class="[item.icon, item.color]" />
              <div>
                <p class="text-lg font-bold" :class="[item.color]">
                  {{ item.count }}
                </p>
                <p class="text-xs text-n-slate-10">{{ item.label }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Loss Categories -->
        <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm p-4">
          <h2 class="text-sm font-semibold text-n-slate-12 mb-3">
            Por Que Nao Converteu?
          </h2>
          <div
            v-if="lossCategories.length === 0"
            class="text-sm text-n-slate-10 py-4 text-center"
          >
            Nenhuma perda registrada no periodo
          </div>
          <div v-else class="flex flex-col gap-2">
            <div
              v-for="item in lossCategories"
              :key="item.key"
              class="flex items-center gap-3"
            >
              <span
                class="w-4 h-4 text-n-slate-10 shrink-0"
                :class="[item.icon]"
              />
              <div class="flex-1">
                <div class="flex items-center justify-between mb-1">
                  <span class="text-sm text-n-slate-12">{{ item.label }}</span>
                  <span class="text-xs text-n-slate-10">{{ item.count }} ({{ item.pct }}%)</span>
                </div>
                <div class="w-full bg-n-alpha-2 rounded-full h-2">
                  <div
                    class="h-2 rounded-full bg-red-400 transition-all"
                    :style="{ width: item.pct + '%' }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Score Distribution -->
      <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm p-4">
        <h2 class="text-sm font-semibold text-n-slate-12 mb-3">
          Distribuicao de Score IA
        </h2>
        <div class="flex items-center gap-4">
          <div
            v-for="item in scoreDistribution"
            :key="item.label"
            class="flex-1 flex flex-col items-center"
          >
            <div class="w-full bg-n-alpha-2 rounded-full h-4 mb-2">
              <div
                class="h-4 rounded-full transition-all"
                :class="[item.color]"
                :style="{ width: Math.max(Number(item.pct), 2) + '%' }"
              />
            </div>
            <p class="text-lg font-bold text-n-slate-12">{{ item.count }}</p>
            <p class="text-xs text-n-slate-10">
              {{ item.label }} ({{ item.pct }}%)
            </p>
          </div>
        </div>
      </div>

      <!-- Daily Digest Table -->
      <div
        v-if="digests.length > 0"
        class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm overflow-hidden"
      >
        <div class="p-4 border-b border-n-weak">
          <h2 class="text-sm font-semibold text-n-slate-12">
            Evolucao Diaria (Ultimos 7 dias)
          </h2>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-n-weak bg-n-alpha-1">
                <th class="text-left p-3 font-medium text-n-slate-10">Data</th>
                <th class="text-center p-3 font-medium text-n-slate-10">
                  Total
                </th>
                <th class="text-center p-3 font-medium text-n-slate-10">
                  Convertidos
                </th>
                <th class="text-center p-3 font-medium text-n-slate-10">
                  Perdidos
                </th>
                <th class="text-center p-3 font-medium text-n-slate-10">
                  Score Medio
                </th>
                <th class="text-center p-3 font-medium text-n-slate-10">
                  Sentimento
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="d in digests"
                :key="d.date"
                class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1"
              >
                <td class="p-3 text-n-slate-12 font-medium">{{ d.date }}</td>
                <td class="p-3 text-center text-n-slate-12">{{ d.total }}</td>
                <td class="p-3 text-center text-emerald-600 font-medium">
                  {{ d.metrics?.converted ?? 0 }}
                </td>
                <td class="p-3 text-center text-red-600 font-medium">
                  {{ d.metrics?.lost ?? 0 }}
                </td>
                <td class="p-3 text-center text-blue-600">
                  {{
                    d.metrics?.avg_ai_score != null
                      ? d.metrics.avg_ai_score.toFixed(1)
                      : '-'
                  }}
                </td>
                <td class="p-3 text-center text-n-slate-10">
                  {{
                    d.metrics?.avg_sentiment != null
                      ? d.metrics.avg_sentiment.toFixed(2)
                      : '-'
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>
